'use client'

// =============================================================================
// VOICE + VIDEO CALLS (chat -> phone / video icon in the header)
//
// How it works
//  - Media goes phone-to-phone with WebRTC. Firestore is only the "signalling" channel:
//      calls/{callId}                      offer, answer, status (ringing / accepted / declined / ...)
//      calls/{callId}/callerCandidates/*   network candidates from the caller
//      calls/{callId}/calleeCandidates/*   network candidates from the person being called
//  - Permission: NO in-app Allow / Deny sheet. The phone's own permission popup is the only one and it
//    appears just ONCE (the first time the mic / camera is needed) - after that the browser remembers the
//    answer and every call starts straight away. Deny = the call does not start (caller) or is declined (callee).
//  - Sound: the ring tone can only ever play once at a time, and only ONE open tab / window rings for a call.
//    The microphone is opened with echo cancellation + noise suppression, and your own camera preview never
//    plays your own microphone back (see keepEchoCancellationOn / claimCall / CallScreen).
//  - Incoming call = full-screen ring screen (green pick-up, red cut) + an Animora notification
//    when the app is in the background (see showBrandedNotification, kind 'call').
//  - In a call: Speaker mute/unmute (on by default) plus a "Loud" full-volume boost that pushes
//    the other person's voice past the phone's normal ceiling for noisy places (Web Audio gain,
//    see useLoudAudio); Mic mute/unmute; red End button. Same controls in 1:1 and group calls.
//  - Video: the camera preview of YOU is mirrored (scaleX(-1)) exactly like a mirror, so your face
//    looks natural to you. The other person still receives - and you see them - un-mirrored.
// =============================================================================

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { collection, doc, getDoc, onSnapshot, query, setDoc, addDoc, updateDoc, where, serverTimestamp } from '@/lib/db'
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Video, VideoOff, Zap } from 'lucide-react'
import { firestore } from '@/lib/db'
import { showBrandedNotification } from '@/lib/push-notifications'
import { isCallDndActive, type CallDnd } from '@/lib/call-dnd'
import { errorMessage, errorWithCode } from '@/lib/errors'

export type CallPeer = { id: string; name: string; avatar: string; username?: string }
export type CallType = 'voice' | 'video'

type Phase = 'calling' | 'connecting' | 'connected' | 'ended'
// calleeOnline: known only for an outgoing call, once the callee's presence doc has been
// checked (see startCall) - undefined while that check is still in flight. Drives the
// "Ringing…" vs "Connecting…" text on the caller's own screen (see callUI below).
type ActiveCall = { id: string; peer: CallPeer; type: CallType; dir: 'out' | 'in'; phase: Phase; endText?: string; calleeOnline?: boolean }
type IncomingCall = { id: string; peer: CallPeer; type: CallType; offer: RTCSessionDescriptionInit }

const RING_TIMEOUT_MS = 45_000
const STALE_CALL_MS = 60_000
// Must match the presence window used app-wide (see PRESENCE_ONLINE_MS in app/page.tsx):
// someone counts as online for this long after their last heartbeat.
const PRESENCE_ONLINE_MS = 100_000

// STUN alone is enough on Wi-Fi, but many mobile networks (5G / CGNAT) need a TURN relay.
// Set NEXT_PUBLIC_TURN_URL / NEXT_PUBLIC_TURN_USERNAME / NEXT_PUBLIC_TURN_CREDENTIAL on Vercel to enable it.
const iceServers = (): RTCIceServer[] => {
  const list: RTCIceServer[] = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }]
  const url = process.env.NEXT_PUBLIC_TURN_URL
  if (url) {
    list.push({
      urls: url.split(',').map((u) => u.trim()).filter(Boolean),
      username: process.env.NEXT_PUBLIC_TURN_USERNAME || '',
      credential: process.env.NEXT_PUBLIC_TURN_CREDENTIAL || '',
    })
  }
  return list
}

const fmtSecs = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

const END_TEXT: Record<string, string> = {
  declined: 'Call declined',
  cancelled: 'Call cancelled',
  ended: 'Call ended',
  missed: 'No answer',
  busy: 'User is busy',
}

// Echo control. The other person's voice comes out of your speaker and your microphone can pick it up again -
// they then hear their own voice come back 2-3 times. echoCancellation / noiseSuppression stop that.
// 'latency' is a valid browser constraint but missing from TS's lib.dom types, so extend the type.
const AUDIO_CONSTRAINTS: MediaTrackConstraints & { latency?: ConstrainDouble } = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
  // Clear voice: capture at the full 48 kHz / 16-bit that Opus (the call codec) uses, with low delay.
  sampleRate: { ideal: 48000 },
  sampleSize: { ideal: 16 },
  latency: { ideal: 0.02 },
}

// Clear picture: HD at a smooth 30 fps (the browser lowers it by itself on a weak connection).
const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: 'user',
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30, max: 30 },
}

// Opus voice settings: forward error correction on (a dropped packet is repaired instead of crackling), no DTX gaps,
// mono voice at 48 kHz and up to 64 kbps = full-band, studio-like speech.
function tuneOpus(sdp: string | undefined): string | undefined {
  if (!sdp) return sdp
  const m = sdp.match(/a=rtpmap:(\d+) opus\/48000\/2/i)
  if (!m) return sdp
  const pt = m[1]
  const want: Record<string, string> = {
    minptime: '10', useinbandfec: '1', usedtx: '0', stereo: '0', 'sprop-stereo': '0', maxaveragebitrate: '64000', maxplaybackrate: '48000', cbr: '0',
  }
  const line = new RegExp(`a=fmtp:${pt} ([^\\r\\n]*)`)
  const found = sdp.match(line)
  if (found) {
    const cur: Record<string, string> = {}
    found[1].split(';').forEach((kv) => { const [k, v] = kv.split('='); if (k) cur[k.trim()] = (v ?? '').trim() })
    const merged = { ...cur, ...want }
    const joined = Object.entries(merged).map(([k, v]) => (v === '' ? k : `${k}=${v}`)).join(';')
    return sdp.replace(line, `a=fmtp:${pt} ${joined}`)
  }
  const joined = Object.entries(want).map(([k, v]) => `${k}=${v}`).join(';')
  return sdp.replace(new RegExp(`(a=rtpmap:${pt} opus\\/48000\\/2[^\\r\\n]*)`), `$1\r\na=fmtp:${pt} ${joined}`)
}

// After the connection is negotiated: give the voice and the picture a generous but safe bitrate, and keep the picture sharp
// (the browser lowers frame rate before resolution when the network is weak).
function tuneSenders(pc: RTCPeerConnection, group = false) {
  pc.getSenders().forEach((sender) => {
    const track = sender.track
    if (!track) return
    try {
      const params = sender.getParameters()
      if (!params.encodings || !params.encodings.length) params.encodings = [{}]
      if (track.kind === 'audio') {
        params.encodings[0].maxBitrate = 64_000
        ;(params.encodings[0] as RTCRtpEncodingParameters & { priority?: string; networkPriority?: string }).priority = 'high'
        ;(params.encodings[0] as RTCRtpEncodingParameters & { networkPriority?: string }).networkPriority = 'high'
        try { (track as MediaStreamTrack & { contentHint?: string }).contentHint = 'speech' } catch { /* ignore */ }
      } else {
        params.encodings[0].maxBitrate = group ? 1_200_000 : 2_500_000
        params.encodings[0].maxFramerate = 30
        try { (params as RTCRtpSendParameters & { degradationPreference?: string }).degradationPreference = 'maintain-resolution' } catch { /* ignore */ }
        try { (track as MediaStreamTrack & { contentHint?: string }).contentHint = 'motion' } catch { /* ignore */ }
      }
      void sender.setParameters(params).catch(() => undefined)
    } catch { /* an older browser: keep the defaults */ }
  })
}

// ---------- call history inside the chat ----------
// When a 1:1 call is over, a small "call log" message (who called, answered or not, how long) is dropped into their chat -
// like WhatsApp / Telegram. The message id is fixed per call (call_<callId>), so even if both phones try to write it only one
// copy can exist. Group calls are not logged.
type CallLogStatus = 'completed' | 'missed' | 'declined' | 'busy'
const loggedCalls = new Set<string>()
function callLogLine(type: CallType, status: CallLogStatus, secs: number, viewerIsCaller: boolean): string {
  const icon = type === 'video' ? '📹' : '📞'
  const word = type === 'video' ? 'video call' : 'voice call'
  if (status === 'completed') return `${icon} ${type === 'video' ? 'Video call' : 'Voice call'} · ${fmtSecs(secs)}`
  if (status === 'declined') return viewerIsCaller ? `${icon} Call declined` : `${icon} Declined ${word}`
  if (status === 'busy') return viewerIsCaller ? `${icon} User was busy` : `${icon} Missed ${word}`
  return viewerIsCaller ? `${icon} No answer` : `${icon} Missed ${word}`
}
function logCall(p: { uid?: string; me: { name: string; avatar: string }; peerId: string; callId: string; callerId: string; type: CallType; status: CallLogStatus; secs: number }) {
  if (!p.uid || !p.peerId || !p.callId || loggedCalls.has(p.callId)) return
  loggedCalls.add(p.callId)
  const uid = p.uid
  const chatId = [uid, p.peerId].sort().join('_')
  const iAmCaller = p.callerId === uid
  const secs = Math.max(0, Math.min(Math.round(p.secs || 0), 86_400))
  void setDoc(doc(firestore, 'chats', chatId, 'messages', `call_${p.callId}`), {
    text: p.type === 'video' ? '📹 Video call' : '📞 Voice call',
    userId: uid,
    senderName: p.me.name || 'Friend',
    senderAvatar: p.me.avatar || '',
    // a missed call stays unread for the person who was called
    read: !(iAmCaller && p.status !== 'completed'),
    createdAt: serverTimestamp(),
    call: { id: p.callId, type: p.type, status: p.status, secs, callerId: p.callerId },
  }).then(() => {
    void setDoc(doc(firestore, 'friends', uid, 'list', p.peerId), { lastMessage: callLogLine(p.type, p.status, secs, iAmCaller), lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
    void setDoc(doc(firestore, 'friends', p.peerId, 'list', uid), { lastMessage: callLogLine(p.type, p.status, secs, !iAmCaller), lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
  }).catch(() => undefined) // the other phone already wrote it, or chat is blocked - nothing to do
}

const rtcConfig = (): RTCConfiguration => ({ iceServers: iceServers(), bundlePolicy: 'max-bundle', iceCandidatePoolSize: 4 })

// Some phones start the microphone with these switched off even when asked - check what was really applied
// and ask again once the track exists.
function keepEchoCancellationOn(stream: MediaStream) {
  stream.getAudioTracks().forEach((track) => {
    try {
      const applied = track.getSettings?.() as MediaTrackSettings | undefined
      if (applied && (applied.echoCancellation === false || applied.noiseSuppression === false || applied.autoGainControl === false)) {
        void track.applyConstraints(AUDIO_CONSTRAINTS).catch(() => undefined)
      }
    } catch { /* ignore */ }
  })
}

// Shared by both the 1:1 hook and the group-call hook below.
// No in-app Allow / Deny sheet: the phone's own permission popup is the only one, and it shows up just once.
async function requestMedia(type: CallType, onFail: (msg: string) => void): Promise<MediaStream | null> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === 'undefined') {
    onFail('Calls are not available on this device.')
    return null
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: AUDIO_CONSTRAINTS,
      video: type === 'video' ? VIDEO_CONSTRAINTS : false,
    })
    keepEchoCancellationOn(stream)
    return stream
  } catch (err) {
    const name = (err as { name?: string })?.name
    onFail(
      name === 'NotAllowedError' || name === 'SecurityError'
        ? errorWithCode(type === 'video' ? 'CALL_NO_CAMERA' : 'CALL_NO_MIC')
        : name === 'NotFoundError' || name === 'OverconstrainedError'
          ? `No ${type === 'video' ? 'camera or microphone' : 'microphone'} found on this device. (${type === 'video' ? 'CALL_NO_CAMERA' : 'CALL_NO_MIC'})`
          : errorWithCode('CALL_FAILED'),
    )
    return null
  }
}

// Only ONE open tab / window of Animora may ring for a call. With Animora open in several tabs (or a tab and the
// installed app) every one of them used to ring at once - slightly out of step - which sounds like the ring tone
// playing 2-3 times. The tab that is on screen claims the call first, the others stay silent. Uses the browser's
// Web Locks (one lock per call id); where they are not available the call simply rings as before.
const claimReleasers = new Map<string, () => void>()
async function claimCall(callId: string): Promise<boolean> {
  const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined
  if (!locks?.request) return true
  if (typeof document !== 'undefined' && document.hidden) await new Promise((r) => setTimeout(r, 700)) // a background tab lets the visible one go first
  return new Promise<boolean>((resolve) => {
    locks.request(`animora-call-${callId}`, { ifAvailable: true }, (lock) => {
      if (!lock) { resolve(false); return undefined }
      resolve(true)
      return new Promise<void>((release) => { claimReleasers.set(callId, release) }) // held until the call is over
    }).catch(() => resolve(true))
  })
}
function releaseClaim(callId: string) {
  claimReleasers.get(callId)?.()
  claimReleasers.delete(callId)
}

async function closeCallNotification(callId: string) {
  try {
    const reg = await navigator.serviceWorker?.getRegistration()
    const list = (await reg?.getNotifications({ tag: `call_${callId}` })) || []
    list.forEach((n) => n.close())
  } catch { /* ignore */ }
}

// ---- ring tone (small WebAudio melody; silent if the browser blocks audio) ----
// One ring at a time: start() while the same ring is already going does nothing, and stop() cuts every beep that is
// still sounding, so nothing rings on after the call is answered, declined or ended.
// 'in' (someone is calling YOU) plays a bright two-note chime ("Animora tune") so it can never be
// mistaken for a plain message ping; 'out' (you are calling someone) plays the old-school soft
// single-tone ringback. Both are a short musical envelope (attack/decay per note), not a flat beep.
function useRinger() {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const tonesRef = useRef<Set<OscillatorNode>>(new Set())
  const kindRef = useRef<'in' | 'out' | null>(null)

  const stop = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = null
    kindRef.current = null
    tonesRef.current.forEach((o) => { try { o.stop() } catch { /* already stopped */ } })
    tonesRef.current.clear()
    // a paused audio context also lets the phone hand the speaker over to the call audio
    try { void ctxRef.current?.suspend() } catch { /* ignore */ }
    try { navigator.vibrate?.(0) } catch { /* ignore */ }
  }, [])

  // Plays one short musical note. type/freq/gain/duration let 'in' and 'out' sound distinct.
  const note = useCallback((ctx: AudioContext, offset: number, freq: number, peak: number, dur: number, type: OscillatorType) => {
    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.value = freq
    gain.gain.setValueAtTime(0.0001, now + offset)
    gain.gain.exponentialRampToValueAtTime(peak, now + offset + 0.04)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + dur)
    osc.connect(gain).connect(ctx.destination)
    tonesRef.current.add(osc)
    osc.onended = () => { tonesRef.current.delete(osc) }
    osc.start(now + offset)
    osc.stop(now + offset + dur + 0.02)
  }, [])

  const start = useCallback((kind: 'in' | 'out') => {
    if (timerRef.current && kindRef.current === kind) return
    stop()
    kindRef.current = kind
    const ring = () => {
      try {
        const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined
        if (!Ctx) return
        const ctx = ctxRef.current || (ctxRef.current = new Ctx())
        if (ctx.state === 'suspended') void ctx.resume()
        if (kind === 'in') {
          // Incoming-call tune: a bright little two-note "ding-dong" played twice, distinct from
          // both the outgoing ringback and every other notification sound in the app.
          note(ctx, 0, 880, 0.22, 0.26, 'triangle') // A5
          note(ctx, 0.22, 659.25, 0.2, 0.3, 'triangle') // E5
          note(ctx, 0.75, 880, 0.22, 0.26, 'triangle')
          note(ctx, 0.97, 659.25, 0.2, 0.3, 'triangle')
        } else {
          // Outgoing ringback: a soft, classic single-tone "purr-purr" (quieter than the incoming tune).
          note(ctx, 0, 425, 0.14, 0.4, 'sine')
          note(ctx, 0.5, 425, 0.14, 0.4, 'sine')
        }
      } catch { /* ignore */ }
      if (kind === 'in') { try { navigator.vibrate?.([300, 150, 300, 150, 300]) } catch { /* ignore */ } }
    }
    ring()
    timerRef.current = setInterval(ring, 2200)
  }, [stop, note])
  useEffect(() => stop, [stop])
  return { start, stop }
}

export function useCalls({
  uid, me, notify, callsEnabled = true, chattingRef, dnd,
}: {
  uid?: string
  me: { name: string; avatar: string }
  notify: (text: string) => void
  // Call Do-Not-Disturb schedule from the profile; inside the window calls auto-decline as busy.
  dnd?: CallDnd
  // "Allow calls" switch from the profile (Settings). When false, nobody can ring this
  // device - incoming calls are auto-declined as busy before the ring screen ever shows.
  callsEnabled?: boolean
  // True while a chat screen (texting a friend) is open on this device. An incoming call
  // is auto-declined as busy in that case too, instead of interrupting the conversation -
  // matches the ring screen's "User is busy" wording other busy calls already use.
  chattingRef?: { current: boolean }
}) {
  const [active, setActive] = useState<ActiveCall | null>(null)
  const [incoming, setIncoming] = useState<IncomingCall | null>(null)
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null)
  const [micOn, setMicOn] = useState(true)
  const [speakerOn, setSpeakerOn] = useState(true)
  const [boostOn, setBoostOn] = useState(false)
  const [secs, setSecs] = useState(0)
  const connectedAtRef = useRef(0) // when the media connected (0 = never): the call length in the chat log comes from this

  const ringer = useRinger()
  const activeRef = useRef<ActiveCall | null>(null)
  const incomingRef = useRef<IncomingCall | null>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const localRef = useRef<MediaStream | null>(null)
  const unsubsRef = useRef<Array<() => void>>([])
  const ringTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dropTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const docReadyRef = useRef(false)
  const pendingCandsRef = useRef<RTCIceCandidateInit[]>([])
  const ringedRef = useRef<Set<string>>(new Set()) // calls this tab has already handled - a call never rings twice
  const goneRef = useRef<Set<string>>(new Set()) // calls the caller already gave up on
  const meRef = useRef(me)
  meRef.current = me
  const notifyRef = useRef(notify)
  notifyRef.current = notify
  const callsEnabledRef = useRef(callsEnabled)
  callsEnabledRef.current = callsEnabled
  const dndRef = useRef(dnd)
  dndRef.current = dnd

  const setActiveBoth = (v: ActiveCall | null | ((c: ActiveCall | null) => ActiveCall | null)) => {
    const next = typeof v === 'function' ? (v as (c: ActiveCall | null) => ActiveCall | null)(activeRef.current) : v
    activeRef.current = next
    setActive(next)
  }
  const setIncomingBoth = (v: IncomingCall | null) => { incomingRef.current = v; setIncoming(v) }

  // ---------- tear everything down ----------
  const releaseMedia = () => {
    unsubsRef.current.forEach((u) => { try { u() } catch { /* ignore */ } })
    unsubsRef.current = []
    if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current)
    ringTimeoutRef.current = null
    if (dropTimerRef.current) clearTimeout(dropTimerRef.current)
    dropTimerRef.current = null
    ringer.stop()
    const pc = pcRef.current
    pcRef.current = null
    if (pc) {
      pc.ontrack = null
      pc.onicecandidate = null
      pc.onconnectionstatechange = null
      try { pc.close() } catch { /* ignore */ }
    }
    localRef.current?.getTracks().forEach((t) => t.stop())
    localRef.current = null
    setLocalStream(null)
    setRemoteStream(null)
    docReadyRef.current = false
    pendingCandsRef.current = []
    setMicOn(true)
    setSpeakerOn(true)
    setBoostOn(false)
    setSecs(0)
  }

  // Drops the call into the chat history (see logCall). Answered = length of the call; otherwise missed / declined / busy.
  const logEnded = (cur: ActiveCall, text: string) => {
    const connectedAt = connectedAtRef.current
    connectedAtRef.current = 0
    const outgoing = cur.dir === 'out'
    let status: CallLogStatus | null = null
    if (connectedAt) status = 'completed'
    else if (text === END_TEXT.declined) status = 'declined'
    else if (text === END_TEXT.busy) status = 'busy'
    else if (text === END_TEXT.missed || text === END_TEXT.cancelled) status = 'missed'
    else if (outgoing && cur.phase === 'calling' && docReadyRef.current && (text === 'Call ended' || text === END_TEXT.ended)) status = 'missed' // caller hung up while it was still ringing
    if (!status) return
    logCall({
      uid, me: meRef.current, peerId: cur.peer.id, callId: cur.id, callerId: outgoing ? (uid || '') : cur.peer.id,
      type: cur.type, status, secs: connectedAt ? Math.max(1, Math.round((Date.now() - connectedAt) / 1000)) : 0,
    })
  }

  // Ends the call on THIS phone (the other side is told through the call document's status).
  const finish = (text: string) => {
    const cur = activeRef.current
    if (cur) { void closeCallNotification(cur.id); releaseClaim(cur.id); logEnded(cur, text) }
    releaseMedia()
    if (cur) {
      setActiveBoth({ ...cur, phase: 'ended', endText: text })
      window.setTimeout(() => { if (activeRef.current?.id === cur.id && activeRef.current.phase === 'ended') setActiveBoth(null) }, 1300)
    }
  }

  // ---------- microphone / camera ----------
  const getMedia = (type: CallType) => requestMedia(type, notifyRef.current)

  // ---------- peer connection ----------
  const createPeer = (callId: string, role: 'caller' | 'callee', stream: MediaStream) => {
    const pc = new RTCPeerConnection(rtcConfig())
    pcRef.current = pc
    stream.getTracks().forEach((t) => pc.addTrack(t, stream))
    const remote = new MediaStream()
    pc.ontrack = (e) => {
      e.streams[0]?.getTracks().forEach((t) => { if (!remote.getTracks().includes(t)) remote.addTrack(t) })
      if (!e.streams[0] && e.track) remote.addTrack(e.track)
      setRemoteStream(new MediaStream(remote.getTracks()))
    }
    const candCol = role === 'caller' ? 'callerCandidates' : 'calleeCandidates'
    pc.onicecandidate = (e) => {
      if (!e.candidate) return
      const init = e.candidate.toJSON()
      // The caller's call document is written only after the offer exists - hold candidates until then.
      if (role === 'caller' && !docReadyRef.current) { pendingCandsRef.current.push(init); return }
      void addDoc(collection(firestore, 'calls', callId, candCol), init).catch(() => undefined)
    }
    let dropTimer: ReturnType<typeof setTimeout> | null = null
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState
      if (st === 'connected') {
        if (dropTimer) clearTimeout(dropTimer)
        if (!connectedAtRef.current) connectedAtRef.current = Date.now()
        setActiveBoth((c) => (c && c.phase !== 'ended' ? { ...c, phase: 'connected' } : c))
        ringer.stop()
      } else if (st === 'disconnected') {
        // brief network blips recover on their own - give it a few seconds
        dropTimer = setTimeout(() => { if (pcRef.current === pc && pc.connectionState !== 'connected') void hangUp('Connection lost') }, 8000)
      } else if (st === 'failed') {
        void hangUp('Connection failed - check your internet')
      }
    }
    return pc
  }

  const listenCandidates = (callId: string, from: 'callerCandidates' | 'calleeCandidates', pc: RTCPeerConnection) => {
    const unsub = onSnapshot(collection(firestore, 'calls', callId, from), (snap) => {
      snap.docChanges().forEach((ch) => {
        if (ch.type === 'added') void pc.addIceCandidate(new RTCIceCandidate(ch.doc.data() as RTCIceCandidateInit)).catch(() => undefined)
      })
    }, () => undefined)
    unsubsRef.current.push(unsub)
  }

  // ---------- caller ----------
  const startCall = async (peer: CallPeer, type: CallType) => {
    if (!uid) return
    if (activeRef.current || incomingRef.current) { notifyRef.current(errorWithCode('CALL_BUSY')); return }
    const who = peer.username ? peer.username.replace(/^@/, '') : peer.name
    if (peer.id === uid) { notifyRef.current(errorWithCode('CALL_SELF')); return }
    // Calls are only allowed between connected people (Allies). Check that FIRST, before asking for the
    // mic/camera or ringing anyone, so the caller gets a clear reason instead of a generic "Call failed".
    try {
      const rel = await getDoc(doc(firestore, 'friends', uid, 'list', peer.id))
      const status = rel.exists() ? (rel.data() as { status?: string } | undefined)?.status : 'none'
      if (!rel.exists() || status === 'removed') { notifyRef.current(errorWithCode('CALL_NOT_CONNECTED', who)); return }
    } catch (err) {
      const code = (err as { code?: string })?.code || ''
      notifyRef.current(errorWithCode(code.includes('permission') ? 'CALL_NOT_CONNECTED' : 'CALL_NETWORK', who))
      return
    }
    // Respect the other person's "Allow Calls" switch (Settings) - don't even ring them.
    try {
      const calleeProfile = await getDoc(doc(firestore, 'users', peer.id))
      if (calleeProfile.exists() && (calleeProfile.data() as { callsEnabled?: boolean })?.callsEnabled === false) {
        notifyRef.current(`${who} has turned off calls.`)
        return
      }
      // Their Do-Not-Disturb window is evaluated with THEIR saved UTC offset, so it's right wherever the caller is.
      if (calleeProfile.exists() && isCallDndActive((calleeProfile.data() as { callDnd?: CallDnd })?.callDnd)) {
        notifyRef.current(`${who} is on Do Not Disturb right now. Try again later.`)
        return
      }
    } catch { /* if this check fails, fall through and let the normal busy/ring flow handle it */ }
    const stream = await getMedia(type)
    if (!stream) return
    if (activeRef.current || incomingRef.current) { stream.getTracks().forEach((t) => t.stop()); return }
    localRef.current = stream
    setLocalStream(stream)

    const ref = doc(collection(firestore, 'calls'))
    docReadyRef.current = false
    pendingCandsRef.current = []
    setActiveBoth({ id: ref.id, peer, type, dir: 'out', phase: 'calling' })
    ringer.start('out')

    // Peek at the callee's presence just to choose the right word on our own screen -
    // "Ringing…" if they're likely to hear it ring right now, "Connecting…" if they're
    // offline and it'll reach them as a push notification instead. Purely cosmetic: the
    // call itself rings/connects exactly the same either way.
    void getDoc(doc(firestore, 'presence', peer.id)).then((snap) => {
      const lastSeen = snap.data()?.lastSeen?.toDate?.()?.getTime?.()
      const online = Boolean(lastSeen && Date.now() - lastSeen < PRESENCE_ONLINE_MS)
      setActiveBoth((c) => (c && c.id === ref.id ? { ...c, calleeOnline: online } : c))
    }).catch(() => undefined)

    try {
      const pc = createPeer(ref.id, 'caller', stream)
      const offer = await pc.createOffer()
      offer.sdp = tuneOpus(offer.sdp)
      await pc.setLocalDescription(offer)
      tuneSenders(pc)
      await setDoc(ref, {
        callerId: uid,
        calleeId: peer.id,
        callerName: meRef.current.name,
        callerAvatar: meRef.current.avatar,
        calleeName: peer.name,
        calleeAvatar: peer.avatar,
        type,
        status: 'ringing',
        offer: { type: offer.type, sdp: offer.sdp },
        startedAt: Date.now(),
        createdAt: serverTimestamp(),
      })
      docReadyRef.current = true
      pendingCandsRef.current.splice(0).forEach((c) => { void addDoc(collection(firestore, 'calls', ref.id, 'callerCandidates'), c).catch(() => undefined) })

      let answered = false
      // If they never pick up (no answer, or auto-busy because they were chatting / on Do Not Disturb), leave a
      // "missed call" alert in THEIR bell so they know someone tried - once per call.
      let missedNoted = false
      const noteMissed = () => {
        if (missedNoted || answered) return
        missedNoted = true
        void addDoc(collection(firestore, 'notifications', peer.id, 'items'), {
          fromId: uid,
          fromName: meRef.current.name || 'Someone',
          fromAvatar: meRef.current.avatar || '',
          type: 'missed_call',
          message: type === 'video' ? 'tried to video call you.' : 'tried to call you.',
          targetId: ref.id,
          read: false,
          createdAt: serverTimestamp(),
        }).catch(() => undefined)
      }
      unsubsRef.current.push(onSnapshot(ref, (snap) => {
        const d = snap.data()
        if (!d || activeRef.current?.id !== ref.id) return
        if (d.answer && !answered && pcRef.current === pc) {
          answered = true
          if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current)
          ringer.stop()
          setActiveBoth((c) => (c ? { ...c, phase: c.phase === 'connected' ? 'connected' : 'connecting' } : c))
          void pc.setRemoteDescription(new RTCSessionDescription(d.answer)).then(() => listenCandidates(ref.id, 'calleeCandidates', pc)).catch(() => undefined)
        }
        if (d.status === 'busy' || d.status === 'missed') noteMissed()
        if (d.status && END_TEXT[d.status]) finish(END_TEXT[d.status])
      }, () => undefined))

      ringTimeoutRef.current = setTimeout(() => {
        if (activeRef.current?.id === ref.id && activeRef.current.phase === 'calling') {
          void updateDoc(ref, { status: 'missed' }).catch(() => undefined)
          noteMissed()
          finish('No answer')
        }
      }, RING_TIMEOUT_MS)
    } catch (err) {
      console.warn('startCall failed', err)
      const code = (err as { code?: string })?.code || ''
      const kind = code.includes('permission') ? 'CALL_NOT_CONNECTED' : code.includes('unavailable') || code.includes('network') ? 'CALL_NETWORK' : 'CALL_FAILED'
      notifyRef.current(errorWithCode(kind, who))
      finish(errorMessage(kind, who))
    }
  }

  // ---------- callee ----------
  const answer = async () => {
    const inc = incomingRef.current
    if (!inc) return
    ringer.stop()
    void closeCallNotification(inc.id)
    const ref = doc(firestore, 'calls', inc.id)
    setIncomingBoth(null) // hides the ring screen; the "removed from ringing" event that follows must not read as a missed call
    const stream = await getMedia(inc.type)
    if (!stream) { void updateDoc(ref, { status: 'declined' }).catch(() => undefined); releaseClaim(inc.id); return }
    localRef.current = stream
    setLocalStream(stream)
    setActiveBoth({ id: inc.id, peer: inc.peer, type: inc.type, dir: 'in', phase: 'connecting' })
    try {
      const pc = createPeer(inc.id, 'callee', stream)
      await pc.setRemoteDescription(new RTCSessionDescription(inc.offer))
      listenCandidates(inc.id, 'callerCandidates', pc)
      const ans = await pc.createAnswer()
      ans.sdp = tuneOpus(ans.sdp)
      await pc.setLocalDescription(ans)
      tuneSenders(pc)
      docReadyRef.current = true
      await updateDoc(ref, { answer: { type: ans.type, sdp: ans.sdp }, status: 'accepted', answeredAt: Date.now() })
      unsubsRef.current.push(onSnapshot(ref, (snap) => {
        const d = snap.data()
        if (!d || activeRef.current?.id !== inc.id) return
        if (d.status && END_TEXT[d.status]) finish(END_TEXT[d.status])
      }, () => undefined))
    } catch (err) {
      console.warn('answer failed', err)
      void updateDoc(ref, { status: 'ended' }).catch(() => undefined)
      notifyRef.current('Could not connect the call.')
      finish('Call failed')
    }
  }

  const decline = () => {
    const inc = incomingRef.current
    if (!inc) return
    ringer.stop()
    void closeCallNotification(inc.id)
    void updateDoc(doc(firestore, 'calls', inc.id), { status: 'declined' }).catch(() => undefined)
    logCall({ uid, me: meRef.current, peerId: inc.peer.id, callId: inc.id, callerId: inc.peer.id, type: inc.type, status: 'declined', secs: 0 })
    releaseClaim(inc.id)
    setIncomingBoth(null)
  }

  // Red button while calling / in a call.
  async function hangUp(text = 'Call ended') {
    const cur = activeRef.current
    if (!cur || cur.phase === 'ended') return
    const status = cur.dir === 'out' && cur.phase === 'calling' ? 'cancelled' : 'ended'
    void updateDoc(doc(firestore, 'calls', cur.id), { status }).catch(() => undefined)
    finish(text)
  }

  // ---------- incoming calls ----------
  useEffect(() => {
    if (!uid) return
    const q = query(collection(firestore, 'calls'), where('calleeId', '==', uid), where('status', '==', 'ringing'))
    return onSnapshot(q, (snap) => {
      snap.docChanges().forEach((ch) => {
        const d = ch.doc.data()
        if (ch.type === 'removed') {
          goneRef.current.add(ch.doc.id)
          // caller gave up (or it timed out) while our ring screen was still up = missed call
          if (incomingRef.current?.id === ch.doc.id) {
            ringer.stop()
            void closeCallNotification(ch.doc.id)
            releaseClaim(ch.doc.id)
            notifyRef.current(`Missed call from ${incomingRef.current.peer.name}`)
            logCall({ uid, me: meRef.current, peerId: incomingRef.current.peer.id, callId: ch.doc.id, callerId: incomingRef.current.peer.id, type: incomingRef.current.type, status: 'missed', secs: 0 })
            setIncomingBoth(null)
          }
          return
        }
        if (ch.type !== 'added') return
        const callId = ch.doc.id
        if (ringedRef.current.has(callId)) return // already handled here - a call never rings twice
        if (Date.now() - Number(d.startedAt || 0) > STALE_CALL_MS || !d.offer) return
        // Auto-decline as "busy" - without ever showing the ring screen or sounding a tone -
        // when: already on another call, calls are turned off in Settings, or this device is
        // currently in a chat conversation (texting a friend). Same wording the caller sees
        // either way (END_TEXT.busy = "User is busy").
        if (activeRef.current || incomingRef.current || !callsEnabledRef.current || chattingRef?.current || isCallDndActive(dndRef.current, Date.now(), -new Date().getTimezoneOffset())) {
          void updateDoc(ch.doc.ref, { status: 'busy' }).catch(() => undefined)
          return
        }
        ringedRef.current.add(callId)
        const peer: CallPeer = { id: String(d.callerId), name: String(d.callerName || 'Someone'), avatar: String(d.callerAvatar || '') }
        const type: CallType = d.type === 'video' ? 'video' : 'voice'
        const offer = d.offer as RTCSessionDescriptionInit

        // Only the tab that wins the claim rings (see claimCall); any other open tab of the app stays silent.
        void claimCall(callId).then((mine) => {
          if (!mine) return
          if (goneRef.current.has(callId) || activeRef.current || incomingRef.current) { releaseClaim(callId); return }
          setIncomingBoth({ id: callId, peer, type, offer })
          ringer.start('in')
          if (typeof document !== 'undefined' && document.hidden) {
            void showBrandedNotification({
              body: `${peer.name} is ${type === 'video' ? 'video calling' : 'calling'} you…`,
              tag: `call_${callId}`,
              renotify: true,
              requireInteraction: true,
              vibrate: [300, 150, 300, 150, 300],
              actions: [{ action: 'answer', title: 'Answer' }, { action: 'decline', title: 'Decline' }],
              data: { kind: 'call', fromId: peer.id, fromName: peer.name, callId, callType: type },
            })
          }
          // the ring screen stops by itself if nobody answers
          window.setTimeout(() => {
            if (incomingRef.current?.id === callId) { ringer.stop(); void closeCallNotification(callId); releaseClaim(callId); setIncomingBoth(null) }
          }, RING_TIMEOUT_MS + 3000)
        })
      })
    }, () => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  // leaving the page / logging out mid-call
  useEffect(() => () => { releaseMedia() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // call timer
  useEffect(() => {
    if (active?.phase !== 'connected') return
    const t = setInterval(() => setSecs((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [active?.phase])

  // mic mute = the microphone track is switched off (the other side hears silence)
  const toggleMic = () => {
    const next = !micOn
    localRef.current?.getAudioTracks().forEach((t) => { t.enabled = next })
    setMicOn(next)
  }
  const toggleSpeaker = () => setSpeakerOn((v) => !v)
  const toggleBoost = () => setBoostOn((v) => !v)

  const callUI = (
    <>
      {incoming && !active && (
        <CallScreen
          peer={incoming.peer}
          type={incoming.type}
          statusText={incoming.type === 'video' ? 'Incoming video call' : 'Incoming voice call'}
          incoming
          onAccept={() => void answer()}
          onEnd={decline}
        />
      )}
      {active && (
        <CallScreen
          peer={active.peer}
          type={active.type}
          statusText={
            active.phase === 'ended' ? (active.endText || 'Call ended')
              : active.phase === 'calling' ? (active.calleeOnline === false ? 'Connecting…' : active.calleeOnline === true ? 'Ringing…' : 'Calling…')
                : active.phase === 'connecting' ? 'Connecting…'
                  : fmtSecs(secs)
          }
          connected={active.phase === 'connected'}
          ended={active.phase === 'ended'}
          localStream={localStream}
          remoteStream={remoteStream}
          micOn={micOn}
          speakerOn={speakerOn}
          boostOn={boostOn}
          onToggleMic={toggleMic}
          onToggleSpeaker={toggleSpeaker}
          onToggleBoost={toggleBoost}
          onEnd={() => void hangUp()}
        />
      )}
    </>
  )

  return {
    startCall, callUI, inCall: Boolean(active || incoming),
    // Exposed so a background push notification's Answer/Decline buttons (handled up in
    // app/page.tsx via the service worker message / cold-start ?na= link) can drive the same
    // ring screen that's already showing, instead of needing their own copy of this logic.
    answerIncoming: answer,
    declineIncoming: decline,
    incomingCallId: incoming?.id,
  }
}

// =============================================================================
// Loud speaker: routes a remote stream's audio through the Web Audio API instead of
// letting the <video>/<audio> element play it directly, so volume can go past 100% for
// noisy environments (a plain HTML element caps out at 1.0). Also handles the plain
// mute (gain 0) so speakerOn/boostOn share one code path. Falls back to muting the
// element itself if Web Audio isn't available for some reason.
// =============================================================================
const BOOST_GAIN = 2.6

function useLoudAudio(mediaRef: { current: HTMLVideoElement | null }, stream: MediaStream | null | undefined, speakerOn: boolean, boostOn: boolean) {
  const ctxRef = useRef<AudioContext | null>(null)
  const gainRef = useRef<GainNode | null>(null)

  useEffect(() => {
    const el = mediaRef.current
    if (el) el.muted = true // audio plays through the graph below, not the element itself
    if (!stream || stream.getAudioTracks().length === 0) return undefined
    try {
      let ctx: AudioContext | null = ctxRef.current
      if (!ctx) {
        const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext
        if (!Ctor) { if (el) el.muted = !speakerOn; return undefined }
        ctx = new Ctor() as AudioContext
        ctxRef.current = ctx
      }
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
      const source = ctx.createMediaStreamSource(stream)
      const gain = ctx.createGain()
      gain.gain.value = !speakerOn ? 0 : boostOn ? BOOST_GAIN : 1
      source.connect(gain)
      gain.connect(ctx.destination)
      gainRef.current = gain
      return () => { try { source.disconnect() } catch { /* ignore */ }; try { gain.disconnect() } catch { /* ignore */ } }
    } catch {
      if (el) el.muted = !speakerOn
      return undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream])

  useEffect(() => {
    if (gainRef.current) gainRef.current.gain.value = !speakerOn ? 0 : boostOn ? BOOST_GAIN : 1
    else if (mediaRef.current && !stream) mediaRef.current.muted = !speakerOn
  }, [speakerOn, boostOn]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { try { ctxRef.current?.close() } catch { /* ignore */ } }, [])
}

// =============================================================================
// Full-screen call screen (ringing, calling and in-call)
// =============================================================================
// Shared by the 1:1 and group call screens: ring pulse, Answer nudge, press feedback on the round buttons.
const CALL_ANIM_CSS = `
        @keyframes cs-ring { 0% { transform: scale(1); opacity: .45 } 100% { transform: scale(1.75); opacity: 0 } }
        @keyframes cs-nudge { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.09) } }
        .cs-btn { transition: transform .12s ease, background .2s ease; box-shadow: 0 6px 18px rgba(0,0,0,.35); -webkit-tap-highlight-color: transparent; cursor: pointer }
        .cs-btn:active { transform: scale(.92) }
        @media (prefers-reduced-motion: reduce) { .cs-ring, .cs-answer { animation: none !important } }
`

function CallScreen({
  peer, type, statusText, incoming, connected, ended, localStream, remoteStream,
  micOn = true, speakerOn = true, boostOn = false, onToggleMic, onToggleSpeaker, onToggleBoost, onAccept, onEnd,
}: {
  peer: CallPeer
  type: CallType
  statusText: string
  incoming?: boolean
  connected?: boolean
  ended?: boolean
  localStream?: MediaStream | null
  remoteStream?: MediaStream | null
  micOn?: boolean
  speakerOn?: boolean
  boostOn?: boolean
  onToggleMic?: () => void
  onToggleSpeaker?: () => void
  onToggleBoost?: () => void
  onAccept?: () => void
  onEnd: () => void
}) {
  const remoteRef = useRef<HTMLVideoElement>(null)
  const localRef = useRef<HTMLVideoElement>(null)
  const isVideo = type === 'video'
  const remoteHasVideo = Boolean(isVideo && connected && remoteStream && remoteStream.getVideoTracks().length > 0)
  const showLocal = Boolean(isVideo && localStream && !incoming)

  useEffect(() => {
    const el = remoteRef.current
    if (!el) return
    if (el.srcObject !== (remoteStream || null)) el.srcObject = remoteStream || null
    if (remoteStream) void el.play().catch(() => undefined)
  }, [remoteStream])

  // The other person's voice: routed through the Web Audio graph below (mute / full-volume
  // boost both happen there), so this element only ever supplies the picture.
  useLoudAudio(remoteRef, remoteStream, speakerOn, boostOn)

  // Your own preview shows the picture ONLY. The microphone track is never attached to this element (and it is muted),
  // so your own voice can never come back out of the speaker - that would sound like an echo of yourself.
  useEffect(() => {
    const el = localRef.current
    if (!el) return
    const preview = localStream ? new MediaStream(localStream.getVideoTracks()) : null
    el.muted = true
    el.volume = 0
    el.srcObject = preview
    if (preview) void el.play().catch(() => undefined)
  }, [localStream, showLocal])

  const circle = (bg: string, color = '#fff'): CSSProperties => ({
    width: 72, height: 72, borderRadius: '50%', display: 'grid', placeItems: 'center', border: 0, background: bg, color, flexShrink: 0,
    backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  })
  const label: CSSProperties = { marginTop: 8, fontSize: '.8rem', fontWeight: 700, color: 'rgba(255,255,255,.75)', textAlign: 'center' }

  return (
    <div
      role="dialog"
      aria-label="Call"
      style={{
        position: 'fixed', left: 0, right: 0, top: 0, width: '100%', maxWidth: 460, margin: '0 auto', height: '100dvh', zIndex: 400,
        background: 'linear-gradient(180deg, #14161c 0%, #0b0c0f 60%)', color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}
    >
      {/* Polish: small animations (rings pulse while calling, Answer button nudges). Switched off for people who asked their phone for less motion. */}
      <style>{CALL_ANIM_CSS}</style>
      {/* Soft blurred copy of their photo behind everything (voice call / waiting for their video). */}
      {!remoteHasVideo && peer.avatar && (
        <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: `url(${peer.avatar})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(48px) saturate(1.2)', transform: 'scale(1.3)', opacity: 0.28, pointerEvents: 'none' }} />
      )}
      {/* The remote element is always mounted: it is what plays the other person's voice, and (video) their picture.
          The other person is shown exactly as their camera sends it - NOT mirrored. */}
      <video
        ref={remoteRef}
        autoPlay
        playsInline
        style={remoteHasVideo
          ? { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#000' }
          : { position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />

      {/* Top: name + status */}
      <div style={{ position: 'relative', zIndex: 2, padding: 'calc(var(--safe-top, 0px) + 1.8rem) 1rem 1.2rem', textAlign: 'center', background: remoteHasVideo ? 'linear-gradient(rgba(0,0,0,.55), transparent)' : 'none' }}>
        <strong style={{ display: 'block', fontSize: '1.6rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{peer.name}</strong>
        <span style={{ display: 'block', marginTop: 6, fontSize: '1.05rem', color: 'rgba(255,255,255,.75)', fontVariantNumeric: 'tabular-nums' }}>{statusText}</span>
      </div>

      {/* Middle: profile picture (voice call, ringing, or before the other person's video arrives) */}
      <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'grid', placeItems: 'center' }}>
        {!remoteHasVideo && (
          <div style={{ position: 'relative', width: 240, height: 240 }}>
            {!connected && !ended && [0, 0.9].map((delay) => (
              <span key={delay} className="cs-ring" aria-hidden style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid rgba(255,255,255,.5)', animation: `cs-ring 2.2s ease-out ${delay}s infinite` }} />
            ))}
            <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', border: '3px solid rgba(255,255,255,.25)', background: '#26282e', display: 'grid', placeItems: 'center', boxShadow: '0 12px 40px rgba(0,0,0,.5)' }}>
              {peer.avatar
                ? <img src={peer.avatar} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <span style={{ fontSize: '5.4rem', fontWeight: 700 }}>{(peer.name || '?').slice(0, 1).toUpperCase()}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Your own camera: small window, MIRRORED like a mirror so left/right feel natural to you. */}
      {showLocal && (
        <video
          ref={localRef}
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute', zIndex: 3, top: 'calc(var(--safe-top, 0px) + 5.2rem)', right: 12, width: 132, aspectRatio: '3 / 4',
            objectFit: 'cover', borderRadius: 16, background: '#111', border: '1.5px solid rgba(255,255,255,.3)',
            transform: 'scaleX(-1)',
          }}
        />
      )}

      {/* Controls */}
      <div style={{ position: 'relative', zIndex: 2, padding: '1rem 1rem calc(1.6rem + env(safe-area-inset-bottom, 0px))', background: remoteHasVideo ? 'linear-gradient(transparent, rgba(0,0,0,.6))' : 'none' }}>
        {incoming ? (
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start' }}>
            <div>
              <button type="button" className="cs-btn" onClick={onEnd} aria-label="Decline" style={{ ...circle('#dc2626'), width: 88, height: 88 }}><PhoneOff size={34} /></button>
              <div style={label}>Decline</div>
            </div>
            <div>
              <button type="button" className="cs-btn cs-answer" onClick={onAccept} aria-label="Answer" style={{ ...circle('#16a34a'), width: 88, height: 88, animation: 'cs-nudge 1.3s ease-in-out infinite' }}>
                {isVideo ? <Video size={34} /> : <Phone size={34} />}
              </button>
              <div style={label}>Answer</div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'flex-start', opacity: ended ? 0.5 : 1, pointerEvents: ended ? 'none' : 'auto' }}>
            <div>
              <button type="button" className="cs-btn" onClick={onToggleSpeaker} aria-label={speakerOn ? 'Mute speaker' : 'Unmute speaker'} style={circle(speakerOn ? 'rgba(255,255,255,.16)' : '#fff', speakerOn ? '#fff' : '#111')}>
                {speakerOn ? <Volume2 size={28} /> : <VolumeX size={28} />}
              </button>
              <div style={label}>{speakerOn ? 'Speaker' : 'Speaker off'}</div>
            </div>
            <div>
              <button
                type="button"
                className="cs-btn"
                onClick={onToggleBoost}
                disabled={!speakerOn}
                aria-label={boostOn ? 'Turn off full volume boost' : 'Turn on full volume boost'}
                style={{ ...circle(boostOn ? 'var(--gold, #e0a721)' : 'rgba(255,255,255,.16)', boostOn ? '#111' : '#fff'), opacity: speakerOn ? 1 : .4 }}
              >
                <Zap size={28} />
              </button>
              <div style={label}>{boostOn ? 'Loud on' : 'Loud'}</div>
            </div>
            <div>
              <button type="button" className="cs-btn" onClick={onToggleMic} aria-label={micOn ? 'Mute microphone' : 'Unmute microphone'} style={circle(micOn ? 'rgba(255,255,255,.16)' : '#fff', micOn ? '#fff' : '#111')}>
                {micOn ? <Mic size={28} /> : <MicOff size={28} />}
              </button>
              <div style={label}>{micOn ? 'Mute' : 'Unmute'}</div>
            </div>
            <div>
              <button type="button" className="cs-btn" onClick={onEnd} aria-label="End call" style={circle('#dc2626')}><PhoneOff size={28} /></button>
              <div style={label}>End</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// GROUP CALLS (guild chat -> phone / video icon in the header, same as a 1:1 chat)
//
// Same phone-to-phone WebRTC idea as above, but with N people instead of 2, so it
// can't be a single offer/answer pair. Each pair of participants gets its own
// direct connection ("mesh"): with everyone connected straight to everyone else,
// no extra relay server is needed, but the data cost of a video call grows with
// the number of people, so a call is capped at MAX_GROUP_PARTICIPANTS live people
// - anyone who tries to join a full call is told it's full and turned away.
//
//   groupCalls/{callId}                          host, guild, type, status, who's invited,
//                                                 and a participants map (name/avatar/status/joinedAt per uid)
//   groupCalls/{callId}/links/{uidA_uidB}         one signalling doc per PAIR that has ever connected:
//                                                 offer + answer between exactly those two people
//   groupCalls/{callId}/links/{pair}/candidates   ICE candidates for that pair, tagged `from` so each
//                                                 side can skip echoing its own candidates back to itself
//
// Who offers to whom: whoever joins LATER always sends the offer to everyone already in the call - that
// is decided once, at join time, from each existing participant's `joinedAt`, so there is never a pair
// where both sides try to offer at once. A participant who is already in the call simply listens for a
// link where they are the `answererId` and answers it when the new person's offer appears.
// =============================================================================

const MAX_GROUP_PARTICIPANTS = 8

type GroupParticipant = { name: string; avatar: string; status: 'ringing' | 'joined' | 'declined' | 'left'; joinedAt: number }
type GroupCallDoc = {
  hostId: string; hostName: string; hostAvatar: string
  chatId: string; chatName: string; chatAvatar: string
  type: CallType
  memberIds: string[]
  participants: Record<string, GroupParticipant>
  status: 'active' | 'ended'
  startedAt: number
}
type ActiveGroupCall = { id: string; chatId: string; chatName: string; type: CallType; phase: 'joining' | 'active' | 'ended' }
type IncomingGroupCall = { id: string; chatId: string; chatName: string; chatAvatar: string; type: CallType; hostName: string; hostAvatar: string; memberCount: number }
type GroupTileData = { id: string; name: string; avatar: string; stream: MediaStream | null }

const groupPairId = (a: string, b: string) => [a, b].sort().join('_')

export function useGroupCalls({
  uid, me, notify, callsEnabled = true, chattingRef, dnd,
}: {
  uid?: string
  me: { name: string; avatar: string }
  notify: (text: string) => void
  dnd?: CallDnd
  callsEnabled?: boolean
  chattingRef?: { current: boolean }
}) {
  const [activeGroup, setActiveGroup] = useState<ActiveGroupCall | null>(null)
  const [incomingGroup, setIncomingGroup] = useState<IncomingGroupCall | null>(null)
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [peers, setPeers] = useState<Record<string, GroupTileData>>({})
  const [micOn, setMicOn] = useState(true)
  const [camOn, setCamOn] = useState(true)
  const [speakerOn, setSpeakerOn] = useState(true)
  const [boostOn, setBoostOn] = useState(false)
  const [secs, setSecs] = useState(0)

  const ringer = useRinger()
  const activeGroupRef = useRef<ActiveGroupCall | null>(null)
  const incomingGroupRef = useRef<IncomingGroupCall | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  const pcMapRef = useRef<Map<string, RTCPeerConnection>>(new Map())
  const unsubsRef = useRef<Array<() => void>>([])
  const groupDocRef = useRef<GroupCallDoc | null>(null)
  const ringedGroupRef = useRef<Set<string>>(new Set())
  const meRef = useRef(me)
  meRef.current = me
  const notifyRef = useRef(notify)
  notifyRef.current = notify
  const callsEnabledRef = useRef(callsEnabled)
  callsEnabledRef.current = callsEnabled
  const dndRef = useRef(dnd)
  dndRef.current = dnd

  const setActiveGroupBoth = (v: ActiveGroupCall | null | ((c: ActiveGroupCall | null) => ActiveGroupCall | null)) => {
    const next = typeof v === 'function' ? (v as (c: ActiveGroupCall | null) => ActiveGroupCall | null)(activeGroupRef.current) : v
    activeGroupRef.current = next
    setActiveGroup(next)
  }
  const setIncomingGroupBoth = (v: IncomingGroupCall | null) => { incomingGroupRef.current = v; setIncomingGroup(v) }

  const releaseMediaGroup = () => {
    unsubsRef.current.forEach((u) => { try { u() } catch { /* ignore */ } })
    unsubsRef.current = []
    ringer.stop()
    pcMapRef.current.forEach((pc) => {
      pc.ontrack = null; pc.onicecandidate = null; pc.onconnectionstatechange = null
      try { pc.close() } catch { /* ignore */ }
    })
    pcMapRef.current.clear()
    localStreamRef.current?.getTracks().forEach((t) => t.stop())
    localStreamRef.current = null
    setLocalStream(null)
    setPeers({})
    setMicOn(true)
    setCamOn(true)
    setSpeakerOn(true)
    setBoostOn(false)
    setSecs(0)
    groupDocRef.current = null
  }

  const endGroupLocally = (text: string) => {
    const cur = activeGroupRef.current
    if (!cur || cur.phase === 'ended') return
    void closeCallNotification(cur.id)
    releaseClaim(cur.id)
    releaseMediaGroup()
    setActiveGroupBoth({ ...cur, phase: 'ended' })
    notifyRef.current(text)
    window.setTimeout(() => { if (activeGroupRef.current?.id === cur.id && activeGroupRef.current.phase === 'ended') setActiveGroupBoth(null) }, 1300)
  }

  // ---------- one WebRTC connection to one other participant ----------
  const createGroupPeer = (callId: string, otherUid: string, stream: MediaStream) => {
    const pc = new RTCPeerConnection(rtcConfig())
    stream.getTracks().forEach((t) => pc.addTrack(t, stream))
    pc.ontrack = (e) => {
      setPeers((prev) => {
        const cur = prev[otherUid]
        if (!cur) return prev
        const tracks = new Set(cur.stream?.getTracks() || [])
        e.streams[0]?.getTracks().forEach((t) => tracks.add(t))
        if (!e.streams[0] && e.track) tracks.add(e.track)
        return { ...prev, [otherUid]: { ...cur, stream: new MediaStream(Array.from(tracks)) } }
      })
    }
    const linkRef = doc(firestore, 'groupCalls', callId, 'links', groupPairId(uid!, otherUid))
    pc.onicecandidate = (e) => {
      if (!e.candidate) return
      void addDoc(collection(linkRef, 'candidates'), { ...e.candidate.toJSON(), from: uid }).catch(() => undefined)
    }
    return pc
  }

  const listenGroupCandidates = (callId: string, otherUid: string, pc: RTCPeerConnection) => {
    const linkRef = doc(firestore, 'groupCalls', callId, 'links', groupPairId(uid!, otherUid))
    const unsub = onSnapshot(collection(linkRef, 'candidates'), (snap) => {
      snap.docChanges().forEach((ch) => {
        if (ch.type !== 'added') return
        const d = ch.doc.data() as Record<string, unknown>
        if (d.from === uid) return // never add our own candidates back to ourselves
        const { from: _from, ...init } = d
        void pc.addIceCandidate(new RTCIceCandidate(init as RTCIceCandidateInit)).catch(() => undefined)
      })
    }, () => undefined)
    unsubsRef.current.push(unsub)
  }

  // I just joined (or am the host) - I offer to everyone already in the call.
  const connectAsOfferer = async (callId: string, other: { id: string; name: string; avatar: string }) => {
    if (!uid || !localStreamRef.current || pcMapRef.current.has(other.id)) return
    setPeers((prev) => ({ ...prev, [other.id]: prev[other.id] || { id: other.id, name: other.name, avatar: other.avatar, stream: null } }))
    const pc = createGroupPeer(callId, other.id, localStreamRef.current)
    pcMapRef.current.set(other.id, pc)
    listenGroupCandidates(callId, other.id, pc)
    try {
      const offer = await pc.createOffer()
      offer.sdp = tuneOpus(offer.sdp)
      await pc.setLocalDescription(offer)
      tuneSenders(pc, true)
      const linkRef = doc(firestore, 'groupCalls', callId, 'links', groupPairId(uid, other.id))
      await setDoc(linkRef, { offererId: uid, answererId: other.id, offer: { type: offer.type, sdp: offer.sdp }, createdAt: Date.now() })
      let answered = false
      const unsub = onSnapshot(linkRef, (snap) => {
        const d = snap.data()
        if (d?.answer && !answered) { answered = true; void pc.setRemoteDescription(new RTCSessionDescription(d.answer)).catch(() => undefined) }
      }, () => undefined)
      unsubsRef.current.push(unsub)
    } catch { /* this one peer failed to connect - the rest of the call carries on */ }
  }

  // Someone already in the call joined before me - I wait for their offer and answer it.
  const watchIncomingLinks = (callId: string) => {
    const q = query(collection(firestore, 'groupCalls', callId, 'links'), where('answererId', '==', uid))
    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach((ch) => {
        if (ch.type !== 'added' && ch.type !== 'modified') return
        const d = ch.doc.data() as { offererId: string; offer?: RTCSessionDescriptionInit }
        const otherUid = String(d.offererId)
        if (!d.offer || pcMapRef.current.has(otherUid) || !localStreamRef.current) return
        const info = groupDocRef.current?.participants?.[otherUid]
        setPeers((prev) => ({ ...prev, [otherUid]: prev[otherUid] || { id: otherUid, name: info?.name || 'Member', avatar: info?.avatar || '', stream: null } }))
        const pc = createGroupPeer(callId, otherUid, localStreamRef.current)
        pcMapRef.current.set(otherUid, pc)
        listenGroupCandidates(callId, otherUid, pc)
        void (async () => {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(d.offer!))
            const answer = await pc.createAnswer()
            answer.sdp = tuneOpus(answer.sdp)
            await pc.setLocalDescription(answer)
            tuneSenders(pc, true)
            await updateDoc(ch.doc.ref, { answer: { type: answer.type, sdp: answer.sdp } })
          } catch { /* this one peer failed to connect - the rest of the call carries on */ }
        })()
      })
    }, () => undefined)
    unsubsRef.current.push(unsub)
  }

  // Keeps tile names/avatars in sync and drops a tile the moment someone leaves/declines.
  const watchGroupDoc = (callId: string) => {
    const ref = doc(firestore, 'groupCalls', callId)
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data() as GroupCallDoc | undefined
      if (!d || d.status === 'ended') { endGroupLocally('Call ended'); return }
      groupDocRef.current = d
      const joinedOthers = Object.entries(d.participants || {}).filter(([pid, p]) => pid !== uid && p.status === 'joined')
      setPeers((prev) => {
        const next: Record<string, GroupTileData> = {}
        joinedOthers.forEach(([pid, p]) => { next[pid] = prev[pid] ? { ...prev[pid], name: p.name, avatar: p.avatar } : { id: pid, name: p.name, avatar: p.avatar, stream: null } })
        Object.keys(prev).forEach((pid) => {
          if (!next[pid]) { const pc = pcMapRef.current.get(pid); if (pc) { try { pc.close() } catch { /* ignore */ } ; pcMapRef.current.delete(pid) } }
        })
        return next
      })
    }, () => undefined)
    unsubsRef.current.push(unsub)
  }

  const joinGroupCall = async (callId: string, type: CallType, chatId: string, chatName: string, preloadedDoc?: GroupCallDoc) => {
    if (!uid || activeGroupRef.current) return
    const stream = await requestMedia(type, notifyRef.current)
    if (!stream) return
    if (activeGroupRef.current) { stream.getTracks().forEach((t) => t.stop()); return }
    localStreamRef.current = stream
    setLocalStream(stream)
    setPeers({})
    setActiveGroupBoth({ id: callId, chatId, chatName, type, phase: 'joining' })

    const ref = doc(firestore, 'groupCalls', callId)
    const data = preloadedDoc || (await getDoc(ref)).data() as GroupCallDoc | undefined
    if (!data || data.status === 'ended') { notifyRef.current('This call has ended.'); releaseMediaGroup(); setActiveGroupBoth(null); return }
    const alreadyJoined = data.participants?.[uid]?.status === 'joined'
    const joinedCount = Object.values(data.participants || {}).filter((p) => p.status === 'joined').length
    if (!alreadyJoined && joinedCount >= MAX_GROUP_PARTICIPANTS) {
      notifyRef.current(`This call is full (max ${MAX_GROUP_PARTICIPANTS} people).`)
      releaseMediaGroup(); setActiveGroupBoth(null); return
    }

    const myJoinedAt = Date.now()
    try {
      await updateDoc(ref, {
        status: 'active',
        [`participants.${uid}.status`]: 'joined',
        [`participants.${uid}.joinedAt`]: myJoinedAt,
        [`participants.${uid}.name`]: meRef.current.name,
        [`participants.${uid}.avatar`]: meRef.current.avatar,
      })
    } catch {
      notifyRef.current('Could not join the call. Please try again.')
      releaseMediaGroup(); setActiveGroupBoth(null); return
    }
    groupDocRef.current = { ...data, participants: { ...data.participants, [uid]: { name: meRef.current.name, avatar: meRef.current.avatar, status: 'joined', joinedAt: myJoinedAt } } }
    watchGroupDoc(callId)
    watchIncomingLinks(callId)
    Object.entries(data.participants || {}).forEach(([pid, p]) => {
      if (pid !== uid && p.status === 'joined') void connectAsOfferer(callId, { id: pid, name: p.name, avatar: p.avatar })
    })
    setActiveGroupBoth((c) => (c && c.id === callId ? { ...c, phase: 'active' } : c))
  }

  const startGroupCall = async (chat: { id: string; name: string; avatar?: string; members: string[] }, type: CallType) => {
    if (!uid) return
    if (activeGroupRef.current || incomingGroupRef.current) { notifyRef.current('You are already in a call.'); return }
    const memberIds = Array.from(new Set([uid, ...(chat.members || [])]))
    if (memberIds.length < 2) { notifyRef.current('Add members to this guild to start a group call.'); return }
    // Best-effort heads-up: members who turned calls off or are on Do Not Disturb won't ring. The call still starts for everyone else.
    void Promise.all(memberIds.filter((m) => m !== uid).slice(0, 30).map((m) => getDoc(doc(firestore, 'users', m)).then((s) => {
      const u = s.exists() ? (s.data() as { callsEnabled?: boolean; callDnd?: CallDnd }) : null
      return Boolean(u && (u.callsEnabled === false || isCallDndActive(u.callDnd)))
    }).catch(() => false))).then((flags) => {
      const n = flags.filter(Boolean).length
      if (n > 0) notifyRef.current(`${n} ${n === 1 ? 'member has' : 'members have'} calls off or Do Not Disturb, so ${n === 1 ? 'they' : 'they'} won't ring.`)
    })
    const ref = doc(collection(firestore, 'groupCalls'))
    const participants: Record<string, GroupParticipant> = {}
    memberIds.forEach((m) => {
      participants[m] = m === uid
        ? { name: meRef.current.name, avatar: meRef.current.avatar, status: 'joined', joinedAt: Date.now() }
        : { name: '', avatar: '', status: 'ringing', joinedAt: 0 }
    })
    const data: GroupCallDoc = {
      hostId: uid, hostName: meRef.current.name, hostAvatar: meRef.current.avatar,
      chatId: chat.id, chatName: chat.name, chatAvatar: chat.avatar || '',
      type, memberIds, participants, status: 'active', startedAt: Date.now(),
    }
    try {
      await setDoc(ref, { ...data, createdAt: serverTimestamp() })
    } catch {
      notifyRef.current('Could not start the call. Please try again.')
      return
    }
    await joinGroupCall(ref.id, type, chat.id, chat.name, data)
  }

  const acceptGroupCall = () => {
    const inc = incomingGroupRef.current
    if (!inc) return
    ringer.stop()
    void closeCallNotification(inc.id)
    setIncomingGroupBoth(null)
    void joinGroupCall(inc.id, inc.type, inc.chatId, inc.chatName)
  }

  const declineGroupCall = () => {
    const inc = incomingGroupRef.current
    if (!inc || !uid) return
    ringer.stop()
    void closeCallNotification(inc.id)
    void updateDoc(doc(firestore, 'groupCalls', inc.id), { [`participants.${uid}.status`]: 'declined' }).catch(() => undefined)
    releaseClaim(inc.id)
    setIncomingGroupBoth(null)
  }

  const leaveGroupCall = async () => {
    const cur = activeGroupRef.current
    if (!cur || !uid) return
    const callId = cur.id
    void closeCallNotification(callId)
    releaseClaim(callId)
    try {
      const ref = doc(firestore, 'groupCalls', callId)
      await updateDoc(ref, { [`participants.${uid}.status`]: 'left' })
      const snap = await getDoc(ref)
      const d = snap.data() as GroupCallDoc | undefined
      const stillJoined = Object.values(d?.participants || {}).filter((p) => p.status === 'joined')
      if (stillJoined.length === 0) await updateDoc(ref, { status: 'ended' })
    } catch { /* ignore - we still leave locally either way */ }
    releaseMediaGroup()
    setActiveGroupBoth(null)
  }

  // ---------- being invited to a group call ----------
  useEffect(() => {
    if (!uid) return
    const q = query(collection(firestore, 'groupCalls'), where('memberIds', 'array-contains', uid))
    return onSnapshot(q, (snap) => {
      snap.docChanges().forEach((ch) => {
        const d = ch.doc.data() as GroupCallDoc
        const callId = ch.doc.id
        if (d.status === 'ended') {
          if (incomingGroupRef.current?.id === callId) { ringer.stop(); void closeCallNotification(callId); releaseClaim(callId); setIncomingGroupBoth(null) }
          if (activeGroupRef.current?.id === callId) endGroupLocally('Call ended')
          return
        }
        if (d.hostId === uid) return // I started it, I'm already in
        const myStatus = d.participants?.[uid]?.status
        if (myStatus !== 'ringing') return // not invited, or I already joined/declined/left
        if (ringedGroupRef.current.has(callId)) return
        if (activeGroupRef.current || incomingGroupRef.current) return
        if (Date.now() - Number(d.startedAt || 0) > STALE_CALL_MS) return
        // Calls turned off, or this device mid-conversation in a chat: quietly skip the ring
        // screen (same "don't interrupt" rule as 1:1 calls) rather than leaving someone marked
        // "ringing" forever in a group call they'll never see.
        if (!callsEnabledRef.current || chattingRef?.current || isCallDndActive(dndRef.current, Date.now(), -new Date().getTimezoneOffset())) {
          void updateDoc(doc(firestore, 'groupCalls', callId), { [`participants.${uid}.status`]: 'declined' }).catch(() => undefined)
          return
        }
        ringedGroupRef.current.add(callId)
        void claimCall(callId).then((mine) => {
          if (!mine) return
          if (activeGroupRef.current || incomingGroupRef.current) { releaseClaim(callId); return }
          setIncomingGroupBoth({ id: callId, chatId: d.chatId, chatName: d.chatName, chatAvatar: d.chatAvatar, type: d.type, hostName: d.hostName, hostAvatar: d.hostAvatar, memberCount: d.memberIds.length })
          ringer.start('in')
          if (typeof document !== 'undefined' && document.hidden) {
            void showBrandedNotification({
              body: `${d.hostName} started a ${d.type === 'video' ? 'video' : 'voice'} group call in ${d.chatName}`,
              tag: `call_${callId}`,
              renotify: true,
              requireInteraction: true,
              vibrate: [300, 150, 300, 150, 300],
              actions: [{ action: 'answer', title: 'Join' }, { action: 'decline', title: 'Decline' }],
              data: { kind: 'call', fromId: d.hostId, fromName: d.hostName, callId, callType: d.type },
            })
          }
          window.setTimeout(() => {
            if (incomingGroupRef.current?.id === callId) { ringer.stop(); void closeCallNotification(callId); releaseClaim(callId); setIncomingGroupBoth(null) }
          }, RING_TIMEOUT_MS + 3000)
        })
      })
    }, () => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  useEffect(() => () => { releaseMediaGroup() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeGroup?.phase !== 'active') return
    const t = setInterval(() => setSecs((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [activeGroup?.phase])

  const toggleMic = () => { const next = !micOn; localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = next }); setMicOn(next) }
  const toggleCam = () => { const next = !camOn; localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = next }); setCamOn(next) }
  const toggleSpeaker = () => setSpeakerOn((v) => !v)
  const toggleBoost = () => setBoostOn((v) => !v)

  const joinedCount = 1 + Object.keys(peers).length

  const groupCallUI = (
    <>
      {incomingGroup && !activeGroup && (
        <GroupCallScreen
          chatName={incomingGroup.chatName}
          type={incomingGroup.type}
          incoming
          statusText={incomingGroup.type === 'video' ? 'Incoming group video call' : 'Incoming group voice call'}
          hostName={incomingGroup.hostName}
          hostAvatar={incomingGroup.hostAvatar}
          memberCount={incomingGroup.memberCount}
          peers={{}}
          onAccept={acceptGroupCall}
          onEnd={declineGroupCall}
        />
      )}
      {activeGroup && (
        <GroupCallScreen
          chatName={activeGroup.chatName}
          type={activeGroup.type}
          phase={activeGroup.phase}
          statusText={activeGroup.phase === 'ended' ? 'Call ended' : activeGroup.phase === 'joining' ? 'Joining…' : `${joinedCount} in call · ${fmtSecs(secs)}`}
          peers={peers}
          localStream={localStream}
          micOn={micOn}
          camOn={camOn}
          speakerOn={speakerOn}
          boostOn={boostOn}
          onToggleMic={toggleMic}
          onToggleCam={toggleCam}
          onToggleSpeaker={toggleSpeaker}
          onToggleBoost={toggleBoost}
          onEnd={() => void leaveGroupCall()}
        />
      )}
    </>
  )

  return {
    startGroupCall, groupCallUI, inGroupCall: Boolean(activeGroup || incomingGroup),
    answerIncomingGroup: acceptGroupCall,
    declineIncomingGroup: declineGroupCall,
    incomingGroupCallId: incomingGroup?.id,
  }
}

// =============================================================================
// One tile in the group call grid: the other person's (or my own) video, or their
// avatar when there's no picture to show (voice call, or their camera is off).
// The remote <video> element stays mounted even with no picture - it's what plays
// their voice - and is just made invisible instead of unmounted (see remoteHasVideo
// in CallScreen above for the same trick in the 1:1 screen).
// =============================================================================
function GroupTile({ stream, name, avatar, isSelf, isVideo, speakerOn = true, boostOn = false }: { stream: MediaStream | null; name: string; avatar: string; isSelf: boolean; isVideo: boolean; speakerOn?: boolean; boostOn?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null)
  const hasVideo = isVideo && Boolean(stream && stream.getVideoTracks().length > 0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const src = isSelf && stream ? new MediaStream(stream.getVideoTracks()) : stream
    if (el.srcObject !== (src || null)) el.srcObject = src || null
    if (src) void el.play().catch(() => undefined)
  }, [stream, isSelf])

  // Other members' voices: routed through the Web Audio graph (mute / loud boost). Never
  // applied to your own tile - that stays hard-muted, same as before, so you never hear
  // an echo of yourself.
  useLoudAudio(ref, isSelf ? null : stream, speakerOn, boostOn)

  return (
    <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#1a1c22', aspectRatio: '3 / 4' }}>
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={isSelf}
        style={hasVideo
          ? { width: '100%', height: '100%', objectFit: 'cover', transform: isSelf ? 'scaleX(-1)' : 'none' }
          : { position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
      {!hasVideo && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: '#26282e', display: 'grid', placeItems: 'center' }}>
            {avatar
              ? <img src={avatar} alt="" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#fff' }}>{(name || '?').slice(0, 1).toUpperCase()}</span>}
          </div>
        </div>
      )}
      <span style={{
        position: 'absolute', left: 6, bottom: 6, fontSize: '.66rem', fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,.5)',
        padding: '2px 6px', borderRadius: 6, maxWidth: '82%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{isSelf ? 'You' : (name || 'Member')}</span>
    </div>
  )
}

// =============================================================================
// Full-screen group call screen (ringing, and the in-call video/avatar grid)
// =============================================================================
function GroupCallScreen({
  chatName, type, statusText, incoming, phase, peers, localStream, micOn, camOn, speakerOn = true, boostOn = false, memberCount, hostName, hostAvatar,
  onAccept, onEnd, onToggleMic, onToggleCam, onToggleSpeaker, onToggleBoost,
}: {
  chatName: string
  type: CallType
  statusText: string
  incoming?: boolean
  phase?: 'joining' | 'active' | 'ended'
  peers: Record<string, GroupTileData>
  localStream?: MediaStream | null
  micOn?: boolean
  camOn?: boolean
  speakerOn?: boolean
  boostOn?: boolean
  memberCount?: number
  hostName?: string
  hostAvatar?: string
  onAccept?: () => void
  onEnd: () => void
  onToggleMic?: () => void
  onToggleCam?: () => void
  onToggleSpeaker?: () => void
  onToggleBoost?: () => void
}) {
  const isVideo = type === 'video'
  const tiles = Object.values(peers)
  const tileCount = tiles.length + 1
  const circle = (bg: string, color = '#fff'): CSSProperties => ({ width: 66, height: 66, borderRadius: '50%', display: 'grid', placeItems: 'center', border: 0, background: bg, color, flexShrink: 0, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' })
  const label: CSSProperties = { marginTop: 6, fontSize: '.68rem', fontWeight: 700, color: 'rgba(255,255,255,.75)', textAlign: 'center' }

  return (
    <div
      role="dialog"
      aria-label="Group call"
      style={{
        position: 'fixed', left: 0, right: 0, top: 0, width: '100%', maxWidth: 460, margin: '0 auto', height: '100dvh', zIndex: 400,
        background: 'linear-gradient(180deg, #14161c 0%, #0b0c0f 60%)', color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}
    >
      <style>{CALL_ANIM_CSS}</style>
      <div style={{ padding: 'calc(var(--safe-top, 0px) + 1.2rem) 1rem .8rem', textAlign: 'center' }}>
        <strong style={{ display: 'block', fontSize: '1.1rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chatName}</strong>
        <span style={{ display: 'block', marginTop: 4, fontSize: '.85rem', color: 'rgba(255,255,255,.7)', fontVariantNumeric: 'tabular-nums' }}>{statusText}</span>
      </div>

      {incoming ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <div style={{ position: 'relative', width: 184, height: 184 }}>
            {[0, 0.9].map((delay) => (
              <span key={delay} className="cs-ring" aria-hidden style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid rgba(255,255,255,.5)', animation: `cs-ring 2.2s ease-out ${delay}s infinite` }} />
            ))}
            <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', border: '3px solid rgba(255,255,255,.25)', background: '#26282e', display: 'grid', placeItems: 'center', boxShadow: '0 12px 40px rgba(0,0,0,.5)' }}>
            {hostAvatar
              ? <img src={hostAvatar} alt="" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: '3.8rem', fontWeight: 700 }}>{(hostName || '?').slice(0, 1).toUpperCase()}</span>}
            </div>
          </div>
          <span style={{ fontSize: '.85rem', color: 'rgba(255,255,255,.7)', textAlign: 'center', padding: '0 1.2rem' }}>
            {hostName} started this call{memberCount && memberCount > 2 ? ` · ${memberCount} members` : ''}
          </span>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 .8rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: tileCount <= 2 ? '1fr' : 'repeat(2, 1fr)', gap: 8 }}>
            <GroupTile stream={localStream || null} name="You" avatar="" isSelf isVideo={isVideo && Boolean(camOn)} />
            {tiles.map((p) => <GroupTile key={p.id} stream={p.stream} name={p.name} avatar={p.avatar} isSelf={false} isVideo={isVideo} speakerOn={speakerOn} boostOn={boostOn} />)}
          </div>
        </div>
      )}

      <div style={{ padding: '1rem 1rem calc(1.4rem + env(safe-area-inset-bottom, 0px))' }}>
        {incoming ? (
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start' }}>
            <div>
              <button type="button" className="cs-btn" onClick={onEnd} aria-label="Decline" style={{ ...circle('#dc2626'), width: 80, height: 80 }}><PhoneOff size={30} /></button>
              <div style={label}>Decline</div>
            </div>
            <div>
              <button type="button" className="cs-btn cs-answer" onClick={onAccept} aria-label="Join call" style={{ ...circle('#16a34a'), width: 80, height: 80, animation: 'cs-nudge 1.3s ease-in-out infinite' }}>
                {isVideo ? <Video size={30} /> : <Phone size={30} />}
              </button>
              <div style={label}>Join</div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'flex-start', opacity: phase === 'ended' ? 0.5 : 1, pointerEvents: phase === 'ended' ? 'none' : 'auto' }}>
            <div>
              <button type="button" className="cs-btn" onClick={onToggleSpeaker} aria-label={speakerOn ? 'Mute speaker' : 'Unmute speaker'} style={circle(speakerOn ? 'rgba(255,255,255,.16)' : '#fff', speakerOn ? '#fff' : '#111')}>
                {speakerOn ? <Volume2 size={20} /> : <VolumeX size={20} />}
              </button>
              <div style={label}>{speakerOn ? 'Speaker' : 'Speaker off'}</div>
            </div>
            <div>
              <button
                className="cs-btn"
                type="button"
                onClick={onToggleBoost}
                disabled={!speakerOn}
                aria-label={boostOn ? 'Turn off full volume boost' : 'Turn on full volume boost'}
                style={{ ...circle(boostOn ? 'var(--gold, #e0a721)' : 'rgba(255,255,255,.16)', boostOn ? '#111' : '#fff'), opacity: speakerOn ? 1 : .4 }}
              >
                <Zap size={20} />
              </button>
              <div style={label}>{boostOn ? 'Loud on' : 'Loud'}</div>
            </div>
            <div>
              <button type="button" className="cs-btn" onClick={onToggleMic} aria-label={micOn ? 'Mute microphone' : 'Unmute microphone'} style={circle(micOn ? 'rgba(255,255,255,.16)' : '#fff', micOn ? '#fff' : '#111')}>
                {micOn ? <Mic size={24} /> : <MicOff size={24} />}
              </button>
              <div style={label}>{micOn ? 'Mute' : 'Unmute'}</div>
            </div>
            {isVideo && (
              <div>
                <button type="button" className="cs-btn" onClick={onToggleCam} aria-label={camOn ? 'Turn camera off' : 'Turn camera on'} style={circle(camOn ? 'rgba(255,255,255,.16)' : '#fff', camOn ? '#fff' : '#111')}>
                  {camOn ? <Video size={24} /> : <VideoOff size={24} />}
                </button>
                <div style={label}>{camOn ? 'Camera' : 'Camera off'}</div>
              </div>
            )}
            <div>
              <button type="button" className="cs-btn" onClick={onEnd} aria-label="Leave call" style={circle('#dc2626')}><PhoneOff size={24} /></button>
              <div style={label}>Leave</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
